import Icon, { CaretRightFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Modal } from 'antd';
import React, { useState } from 'react';
import { ReactComponent as IconMsg } from 'assets/icons/B-mjsd.svg';
import { LocalMessage } from 'hooks/business/conversation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import { trans } from 'hooks/business/sendMessage';
import BubbleMessage, { BubbleMessageProps } from './BubbleMessage';
import { openExternalURL } from 'utils/openExternalURL';
import { useTransAppProductName } from 'hooks/useTrans';
import ProxyVideo from 'components/ProxyVideo';

export interface TextVideoMessageProps extends Pick<BubbleMessageProps, 'color'> {
  className?: string;
  msg: LocalMessage;
  layout: 'left' | 'right';
  color: string;
}
const modalCss = css`
  top: 0;
  width: 100vw !important;
  max-width: 100vw;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  padding: 0;
  margin: 0;
  .ant-modal-content {
    width: 100%;
    overflow: hidden;
    height: 100%;
    .ant-modal-body {
      width: 100%;
      height: 100%;
      height: calc(100vh - 55px);
      padding: 0;
      overflow: auto;
      background-color: #eef0f4;
    }
  }
`;
const Section = styled.section<{ color: string }>`
  color: ${({ color }) => color};
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: pre-line;
  font-size: 14px;
  border-bottom: 1px solid #c3dbf0;
  padding: 8px 0;
  .link {
    color: #336fff;
    cursor: pointer;
    margin-top: 4px;
    margin-bottom: 4px;
  }
`;

const StyledBubbleMessage = styled(BubbleMessage)`
  position: relative;
  padding: 0 16px;
  .translate-btn {
    display: none;
  }
  &:hover {
    .translate-btn {
      display: flex;
      justify-content: center;
    }
  }
  .translate-status {
    font-size: 12px;
    margin-top: 6px;
    display: flex;
    gap: 4px;
    align-items: center;
    &.success {
      color: #84868c;
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;
const ModalContent = styled.div`
  width: 100%;
  margin: 0;
  height: 100%;
  padding: 20px;
  video {
    margin: 0;
    width: 100%;
    height: 100%;
  }
`;
const VideoBoxContainer = styled.div`
  &.video-box {
    display: flex;
    margin: 12px 0;
    padding: 4px;
    background: rgba(237, 245, 255, 1);
    border-radius: 2px;
    .left {
      position: relative;
      height: 78px;
      .thumb {
        width: 140px;
        height: 78px;
        object-fit: cover;
        border-radius: 2px;
        cursor: pointer;
      }
      .duration {
        position: absolute;
        bottom: 6px;
        right: 12px;
        color: #ffffff;
        font-size: 12px;
        border-radius: 12px;
        opacity: 0.47;
        padding: 3px 6px;
        background-color: rgba(35, 36, 38, 1);
      }
      .play {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: #ffffff;
        z-index: 10;
        width: 24px;
        height: 24px;
        background-color: rgba(51, 51, 51, 0.6);
        border-radius: 100px;
        display: flex;
        justify-content: center;
        align-items: center;
        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
    .right {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding-left: 7px;
      color: #84868c;
      a {
        padding: 0;
        text-align: left;
      }
    }
  }
`;
const Description = ({
  url,
  buyerView,
  text,
}: {
  text: string;
  url: string;
  buyerView?: boolean;
}) => {
  const { t } = useTranslate();
  return (
    <Section color="#515359">
      {text}:
      <div className="link" onClick={() => openExternalURL(url)}>
        {url}
      </div>
      <div className="translate-status success">
        <Icon component={IconMsg} />
        {t(buyerView ? 'Entities.买家收到' : 'Entities.翻译完成')}
      </div>
    </Section>
  );
};

export const VideoBox = ({
  videoContent,
}: {
  videoContent: Conversation.MsgTextVideo['videoContent'];
}) => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [play, setPlay] = useState(false);
  const { durationSeconds = 0 } = videoContent;
  const duration = () => {
    const minute = Math.floor(durationSeconds / 60);
    const second = durationSeconds % 60;
    return `${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
  };
  return (
    <VideoBoxContainer className="video-box" onClick={(e) => e.stopPropagation()}>
      <div className="left" onClick={() => setPlay(true)}>
        <img src={videoContent.thumbUrl} alt="" className="thumb" />
        <div className="play">
          <CaretRightFilled />
        </div>

        <div className="duration">{duration()}</div>
      </div>
      <div className="right">
        <div>{appProductNameT('Entities.videoAvailableTips')}</div>
        <a href={videoContent.localVideoUrl} download>
          {t('Entities.下载')}
        </a>
      </div>
      <Modal
        width={700}
        visible={play}
        title={videoContent.videoName}
        destroyOnClose
        footer={null}
        className={modalCss}
        onCancel={() => setPlay(false)}
      >
        <ModalContent>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <ProxyVideo
            controls
            autoPlay
            src={videoContent.localVideoUrl}
            controlsList="nodownload"
          />
        </ModalContent>
      </Modal>
    </VideoBoxContainer>
  );
};

const TextVideoMessage: React.FC<TextVideoMessageProps> = (props) => {
  const { color, msg, layout } = props;
  const sysLang = useSystemLanguageValue();
  const { t } = useTranslate();
  const { textContent, videoContent } = msg.textVideoContent!;
  const translation = trans[sysLang];
  return (
    <StyledBubbleMessage color={color} className={layout}>
      <Description url={videoContent.videoUrl} buyerView text={textContent.text.split('http')[0]} />
      {translation && <Description url={videoContent.videoUrl} text={t('Entities.点击查看视频')} />}
      <VideoBox videoContent={videoContent} />
    </StyledBubbleMessage>
  );
};
export default TextVideoMessage;
